/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Autocomplete, Box, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import type { LocationSearchTypes } from '@opengeoweb/core';
import { useLocationDetail, useLocationList } from '@opengeoweb/core';
import { CustomIconButton, useDebounce } from '@opengeoweb/shared';
import { Close } from '@opengeoweb/theme';
import { useDataExplorerTranslation } from '../../utils/i18n';

export interface LocationSearchProps {
  onSelectLocation?: (location?: LocationSearchTypes.LocationDetail) => void;
}

export const AreaFilterLocationSearch: React.FC<LocationSearchProps> = ({
  onSelectLocation = (): void => {},
}: LocationSearchProps) => {
  const { t } = useDataExplorerTranslation();
  const [inputValue, setInputValue] = useState('');
  const [selectedValue, setSelectedValue] =
    useState<LocationSearchTypes.LocationListResult | null>(null);

  const debouncedInputValue = useDebounce(inputValue, 300);
  const { data: locationListData } = useLocationList(debouncedInputValue);
  const { data: locationDetail, isFetching: isFetchingDetail } =
    useLocationDetail(selectedValue);

  useEffect(() => {
    if (isFetchingDetail) {
      return;
    }
    onSelectLocation(selectedValue ? locationDetail : undefined);
  }, [locationDetail, onSelectLocation, selectedValue, isFetchingDetail]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        pointerEvents: 'all',
        height: 40,
        width: '100%',
        padding: '0px 12px',
      }}
    >
      <Autocomplete
        clearIcon={null}
        disablePortal
        filterOptions={(x) => x}
        getOptionKey={(option) => option.id + option.name}
        getOptionLabel={(option) => option.name}
        inputValue={inputValue}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        noOptionsText={t('search-no-results')}
        onChange={(_event, newValue) => {
          if (newValue && typeof newValue !== 'string') {
            setSelectedValue(newValue);
            setInputValue(newValue.name);
          }
        }}
        options={locationListData || []}
        popupIcon={null}
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus
            data-testid="area-filter-location-search"
            fullWidth
            label={t('dataexplorer-area-search-title')}
            onChange={(event) => {
              setInputValue(event.target.value);
              if (selectedValue) {
                setSelectedValue(null);
              }
            }}
            size="medium"
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <CustomIconButton
                    onClick={() => {
                      setInputValue('');
                      setSelectedValue(null);
                    }}
                    sx={{ marginRight: 1 }}
                    tooltipTitle={t('search-clear')}
                  >
                    <Close />
                  </CustomIconButton>
                ),
              },
            }}
            variant="filled"
          />
        )}
        selectOnFocus
        sx={{
          width: '100%',
          '&.MuiAutocomplete-root .MuiFilledInput-root': {
            height: 40,
            padding: '0px!important',
            '&:before, &:hover:before': {
              borderColor: 'transparent',
            },
          },
          '&.MuiAutocomplete-root .MuiInputLabel-root': {
            lineHeight: '16px',
            maxWidth: 'calc(100% - 40px)',
            ':not(.MuiInputLabel-shrink)': {
              transform: 'translate(12px, 12px) scale(1)',
            },
          },
          '&.MuiAutocomplete-root .MuiFilledInput-input': {
            lineHeight: '20px',
            paddingLeft: '12px',
            paddingTop: '20px',
          },
        }}
        value={selectedValue}
      />
    </Box>
  );
};
